// Single fan-out for conversion events across every platform (GA4, PostHog). Callers fire one
// canonical name; each platform decides whether to record it. Add a platform by adding a sink here.
//
// trackEvent resolves to whether gtag.js was present to send the event — the extension-install tracker
// uses it to gate its "counted" cookie; fire-and-forget callers ignore it.

// Generous: a timeout only defers a count to the next pageview, but a false success can burn a conversion.
const GTAG_LOAD_TIMEOUT_MS = 5000;

// beacon: both platforms default to XHR, which the browser cancels on unload. Resolves false rather than
// awaiting gtag — its confirmation callback can't run past unload either.
export async function trackEvent(name, params = {}, { beacon = false } = {}) {
  if (!name) return false;
  // Mirror to the ad-blocker-resilient platform before the gtag guard — blockers strip gtag far more often.
  window.posthog?.capture(name, params, beacon ? { transport: 'sendBeacon' } : undefined);
  if (typeof gtag === 'undefined') return false;
  gtag('event', name, beacon ? { ...params, transport_type: 'beacon' } : params);
  sendAdsConversion(name, beacon);
  return beacon ? false : gtagDelivered();
}

// gtag applies ga-disable to Analytics destinations only — the Ads destination ignores it, so the
// per-browser opt-out holds only because we read the flag ourselves before touching Ads (UNS-5219).
export function adsDestinationEnabled() {
  const id = window.GOOGLE_ADS_CONVERSION_ID;
  return !!id && !window[`ga-disable-${id}`];
}

// Ads does no dedup across conversion actions, so whatever fires here must be secondary in the Ads
// UI while the GA4 action importing the same event is primary, or every conversion counts twice.
function sendAdsConversion(name, beacon) {
  if (!adsDestinationEnabled()) return;

  const label = window.GOOGLE_ADS_CONVERSION_LABELS?.[name];
  if (!label) return;

  const params = { send_to: `${window.GOOGLE_ADS_CONVERSION_ID}/${label}` };
  gtag('event', 'conversion', beacon ? { ...params, transport_type: 'beacon' } : params);
}

// window.gtag is a dataLayer shim that exists even if Google's script is blocked, so only this callback —
// which only gtag.js can run — proves our queued event drained. It doesn't prove the hit reached Google.
function gtagDelivered() {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), GTAG_LOAD_TIMEOUT_MS);
    gtag('get', window.GOOGLE_ANALYTICS_ID, 'client_id', () => {
      clearTimeout(timer);
      resolve(true);
    });
  });
}
